(() => {
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const lerp = (from, to, progress) => from + (to - from) * progress;
  const smooth = (from, to, value) => {
    const progress = clamp((value - from) / (to - from));
    return progress * progress * (3 - 2 * progress);
  };
  const windowed = (progress, enterStart, enterEnd, exitStart, exitEnd) =>
    smooth(enterStart, enterEnd, progress) * (1 - smooth(exitStart, exitEnd, progress));

  const splitWords = (element) => {
    if (!element || element.dataset.nrSplit === 'true') return [...(element?.querySelectorAll('.nr-word') || [])];
    const words = element.textContent.trim().split(/\s+/);
    element.textContent = '';
    words.forEach((word, index) => {
      const mask = document.createElement('span');
      const inner = document.createElement('span');
      mask.className = 'nr-word-mask';
      inner.className = 'nr-word';
      inner.textContent = word;
      inner.dataset.nrWordIndex = index;
      mask.appendChild(inner);
      element.appendChild(mask);
      if (index < words.length - 1) element.appendChild(document.createTextNode(' '));
    });
    element.dataset.nrSplit = 'true';
    return [...element.querySelectorAll('.nr-word')];
  };

  const buildPanels = (section) => {
    const copy = section.querySelector('.nr-copy');
    if (!copy || copy.dataset.nrPanels === 'true') {
      return {
        right: copy?.querySelector('.nr-story-right'),
        left: copy?.querySelector('.nr-story-left'),
      };
    }

    const right = document.createElement('div');
    const left = document.createElement('div');
    right.className = 'nr-story-panel nr-story-right';
    left.className = 'nr-story-panel nr-story-left';

    ['.nr-index', 'h3', ':scope > p'].forEach((selector) => {
      const element = copy.querySelector(selector);
      if (element) right.appendChild(element);
    });
    ['.nr-specs', '.nr-why', '.nr-actions'].forEach((selector) => {
      const element = copy.querySelector(selector);
      if (element) left.appendChild(element);
    });

    copy.append(right, left);
    copy.dataset.nrPanels = 'true';
    return { right, left };
  };

  const setPx = (element, name, value) => element.style.setProperty(name, `${value.toFixed(2)}px`);
  const setNumber = (element, name, value) => element.style.setProperty(name, value.toFixed(3));

  const bootScene = (root) => {
    if (root.dataset.nrSequenceReady === 'true') return;
    root.dataset.nrSequenceReady = 'true';
    root.classList.add('nr-radian-motion', 'nr-stable-composition', 'nr-js', 'nr-loaded');

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const hero = root.querySelector('.nr-hero');
    const heroHeading = root.querySelector('.nr-hero h2');
    if (heroHeading && heroHeading.textContent.trim() === 'Built For Wild Water.') {
      heroHeading.textContent = 'Built for the Wild.';
    }
    if (heroHeading) heroHeading.textContent = 'Built for the Wild.';
    const heroWords = splitWords(heroHeading);
    const scenes = [...root.querySelectorAll('.nr-product')].map((section) => {
      const panels = buildPanels(section);
      return {
        section,
        right: panels.right,
        left: panels.left,
        words: splitWords(panels.right?.querySelector('h3')),
        specs: [...section.querySelectorAll('.nr-spec')],
      };
    });
    const manifesto = root.querySelector('.nr-manifesto');
    let frameRequested = false;

    const renderWords = (words, progress, stagger = .08) => {
      words.forEach((word, index) => {
        const wordProgress = smooth(index * stagger, .48 + index * stagger, progress);
        setPx(word, '--nr-word-y', lerp(62, 0, wordProgress));
        setNumber(word, '--nr-word-opacity', lerp(.04, 1, wordProgress));
      });
    };

    const render = () => {
      frameRequested = false;
      if (reduceMotion) return;

      const vh = window.innerHeight || 1;
      const mobile = window.innerWidth <= 860;

      if (hero) {
        const containerRect = hero.parentElement.getBoundingClientRect();
        const progress = clamp(-containerRect.top / Math.max(1, containerRect.height - vh));
        setPx(hero, '--nr-hero-y', lerp(0, -70, progress));
        setNumber(hero, '--nr-hero-scale', lerp(1, .94, progress));
        setNumber(hero, '--nr-hero-opacity', lerp(1, .12, smooth(.42, 1, progress)));
        renderWords(heroWords, 1 - progress * .78, .07);
      }

      scenes.forEach(({ section, right, left, words, specs }) => {
        const rect = section.getBoundingClientRect();
        const progress = clamp((vh - rect.top) / (rect.height + vh));
        const imagePresence = windowed(progress, .015, .14, .88, .99);
        const rightPresence = windowed(progress, .19, .36, .48, .61);
        const leftPresence = windowed(progress, .54, .69, .86, .97);

        setNumber(section, '--nr-image-opacity', lerp(.04, 1, imagePresence));
        setNumber(section, '--nr-image-scale', lerp(.82, 1.035, smooth(.03, .28, progress)) - smooth(.88, 1, progress) * .08);
        setPx(section, '--nr-image-y', mobile ? lerp(26, -24, progress) : lerp(70, -58, progress));
        section.style.setProperty('--nr-image-rotate', `${lerp(-1.2, 1, progress).toFixed(2)}deg`);

        setNumber(section, '--nr-right-opacity', rightPresence);
        setPx(section, '--nr-right-x', mobile ? 0 : lerp(74, 0, smooth(.19, .36, progress)) + smooth(.48, .61, progress) * 30);
        setPx(section, '--nr-right-y', mobile ? lerp(34, 0, smooth(.19, .36, progress)) : 0);

        setNumber(section, '--nr-left-opacity', leftPresence);
        setPx(section, '--nr-left-x', mobile ? 0 : lerp(-74, 0, smooth(.54, .69, progress)) - smooth(.86, .97, progress) * 30);
        setPx(section, '--nr-left-y', mobile ? lerp(34, 0, smooth(.54, .69, progress)) : 0);

        renderWords(words, smooth(.19, .4, progress) * (1 - smooth(.48, .61, progress)), .065);
        specs.forEach((spec, index) => {
          const reveal = smooth(.56 + index * .045, .72 + index * .045, progress);
          setPx(spec, '--nr-spec-y', lerp(42, 0, reveal));
          setNumber(spec, '--nr-spec-opacity', reveal);
          setPx(spec, '--nr-spec-blur', lerp(7, 0, reveal));
        });

        if (right) right.setAttribute('aria-hidden', rightPresence < .08 ? 'true' : 'false');
        if (left) left.setAttribute('aria-hidden', leftPresence < .08 ? 'true' : 'false');
      });

      if (manifesto) {
        const rect = manifesto.getBoundingClientRect();
        const progress = smooth(.08, .72, clamp((vh - rect.top) / (vh + rect.height)));
        setPx(manifesto, '--nr-manifesto-y', lerp(92, 0, progress));
        setNumber(manifesto, '--nr-manifesto-opacity', lerp(.15, 1, progress));
      }
    };

    const requestRender = () => {
      if (frameRequested) return;
      frameRequested = true;
      requestAnimationFrame(render);
    };

    render();
    window.addEventListener('scroll', requestRender, { passive: true });
    window.addEventListener('resize', requestRender, { passive: true });
  };

  const boot = () => document.querySelectorAll('.norlan-radian').forEach(bootScene);
  boot();
  document.addEventListener('DOMContentLoaded', boot, { once: true });
  window.addEventListener('load', boot, { once: true });
  document.addEventListener('shopify:section:load', boot);
})();
